import { lowerBound, pointsInRange } from "./range-index";
import { segmentByGap } from "./segment";

/** One `(x, y)` point in a sorted numeric series. */
export type NumericPoint<Y = number> = readonly [number, Y];

/** Copy and sort points by x-coordinate. */
export function sortPoints<Y>(
  points: readonly NumericPoint<Y>[],
): NumericPoint<Y>[] {
  const indexed = points.map((point, index) => ({ point, index }));
  indexed.sort((a, b) => {
    if (a.point[0] !== b.point[0]) return a.point[0] - b.point[0];
    return a.index - b.index;
  });

  const next: NumericPoint<Y>[] = [];
  for (const entry of indexed) {
    next.push(entry.point);
  }

  return next;
}

/** Immutable sorted series keyed by a numeric x-domain. */
export class NumericSeries<Y = number> {
  protected constructor(protected readonly pointsInner: readonly NumericPoint<Y>[]) {}

  /** Build a series from unsorted points. */
  static fromPoints<Y>(points: readonly NumericPoint<Y>[]): NumericSeries<Y> {
    return new NumericSeries(sortPoints(points));
  }

  /** Read points in sorted order. */
  points(): readonly NumericPoint<Y>[] {
    return [...this.pointsInner];
  }

  /** Slice the series by x-range `[start, end)`. */
  range(start: number, end: number): NumericSeries<Y> {
    return new NumericSeries(pointsInRange(this.pointsInner, start, end));
  }

  /** Replace points in `[start, end)` with a new chunk. */
  replaceRange(
    start: number,
    end: number,
    replacement: readonly NumericPoint<Y>[],
  ): NumericSeries<Y> {
    const startIndex = lowerBound(this.pointsInner, start);
    const endIndex = lowerBound(this.pointsInner, end);
    const next: NumericPoint<Y>[] = [];

    for (let i = 0; i < startIndex; i++) {
      next.push(this.pointsInner[i]);
    }

    for (const point of sortPoints(replacement)) {
      next.push(point);
    }

    for (let i = endIndex; i < this.pointsInner.length; i++) {
      next.push(this.pointsInner[i]);
    }

    return new NumericSeries(next);
  }

  /** Split the series into contiguous segments. */
  segmentByGap(maxGap: number): readonly NumericSeries<Y>[] {
    const segments = segmentByGap(this.pointsInner, maxGap);
    const result: NumericSeries<Y>[] = [];
    for (const segment of segments) {
      result.push(new NumericSeries(segment));
    }
    return result;
  }

  /** Transform points and re-sort them by x-coordinate. */
  mapPoints<Next>(
    fn: (point: NumericPoint<Y>) => NumericPoint<Next>,
  ): NumericSeries<Next> {
    const next: NumericPoint<Next>[] = [];
    for (const point of this.pointsInner) {
      next.push(fn(point));
    }
    return NumericSeries.fromPoints(next);
  }

  /** Number of points in the series. */
  get length(): number {
    return this.pointsInner.length;
  }

  /** Smallest x-coordinate, if any. */
  minX(): Option<number> {
    if (this.pointsInner.length === 0) return null;
    return this.pointsInner[0][0];
  }

  /** Largest x-coordinate, if any. */
  maxX(): Option<number> {
    if (this.pointsInner.length === 0) return null;
    return this.pointsInner[this.pointsInner.length - 1][0];
  }
}
